'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Comment {
  id: string;
  author: {
    name: string;
    avatar: string;
    alt: string;
  };
  content: string;
  timestamp: string;
}

interface UpdateFeedItemProps {
  update: {
    id: string;
    type: 'task' | 'expense' | 'comment' | 'system';
    author: {
      name: string;
      avatar: string;
      alt: string;
    };
    content: string;
    timestamp: string;
    comments?: Comment[];
  };
  onAddComment: (updateId: string, comment: string) => void;
}

const UpdateFeedItem = ({ update, onAddComment }: UpdateFeedItemProps) => {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');

  const getTypeIcon = () => {
    switch (update.type) {
      case 'task':
        return { name: 'CheckCircleIcon' as const, color: 'text-success' };
      case 'expense':
        return { name: 'CurrencyDollarIcon' as const, color: 'text-accent' };
      case 'comment':
        return { name: 'ChatBubbleLeftIcon' as const, color: 'text-primary' };
      case 'system':
        return { name: 'BellIcon' as const, color: 'text-muted-foreground' };
      default:
        return { name: 'InformationCircleIcon' as const, color: 'text-muted-foreground' };
    }
  };

  const getTypeBadgeColor = () => {
    switch (update.type) {
      case 'task':
        return 'bg-success/10 text-success';
      case 'expense':
        return 'bg-accent/10 text-accent';
      case 'comment':
        return 'bg-primary/10 text-primary';
      case 'system':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleSubmitComment = () => {
    if (newComment.trim()) {
      onAddComment(update.id, newComment);
      setNewComment('');
    }
  };

  const icon = getTypeIcon();

  return (
    <div className="glass p-3 lg:p-4 hover:shadow-warm-sm transition-smooth">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-full overflow-hidden border-2 border-sage-200/30 flex-shrink-0">
          <AppImage
            src={update.author.avatar}
            alt={update.author.alt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 min-w-0">
              <h4 className="font-caption font-medium text-foreground text-sm lg:text-base truncate">
                {update.author.name}
              </h4>
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-caption font-medium ${getTypeBadgeColor()}`}
              >
                <Icon name={getTypeIcon() as any} size={12} />
                {update.type}
              </span>
            </div>
            <span className="text-xs font-caption text-muted-foreground whitespace-nowrap">
              {update.timestamp}
            </span>
          </div>
          <p className="text-xs lg:text-sm text-foreground mb-3 break-words">{update.content}</p>

          {update.comments && update.comments.length > 0 && (
            <button
              onClick={() => setShowComments(!showComments)}
              className="flex items-center gap-1 text-xs font-caption text-primary hover:text-primary/80 transition-smooth mb-2"
            >
              <Icon
                name={showComments ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                size={14}
              />
              {showComments ? 'Hide' : 'Show'} {update.comments.length} comment{update.comments.length !== 1 ? 's' : ''}
            </button>
          )}

          {showComments && update.comments && (
            <div className="space-y-3 mb-3 pl-3 lg:pl-4 border-l-2 border-sage-200/30">
              {update.comments.map((comment) => (
                <div key={comment.id} className="flex gap-2">
                  <div className="w-6 h-6 rounded-full overflow-hidden border border-sage-200/30 flex-shrink-0">
                    <AppImage
                      src={comment.author.avatar}
                      alt={comment.author.alt}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-caption font-medium text-foreground">
                        {comment.author.name}
                      </span>
                      <span className="text-xs font-caption text-muted-foreground">
                        {comment.timestamp}
                      </span>
                    </div>
                    <p className="text-xs text-foreground">{comment.content}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSubmitComment()}
              placeholder="Add a comment..."
              className="flex-1 px-3 py-2 text-sm font-caption bg-muted border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-smooth"
            />
            <button
              onClick={handleSubmitComment}
              disabled={!newComment.trim()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-caption font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateFeedItem;